import { apiBase } from './apiBase';
import { apiFetch } from './api';
import { useAuthStore } from '../store/useAuthStore';
import { postFileWithProgress, type UploadProgressHandler } from './fileUpload';

// ---------------------------------------------------------------------------
// Bank statements API client (CLE-140).
//
// Mirrors backend/src/routes/bankStatements.ts:
//   GET  /api/bank-statements                 — paginated list
//   POST /api/bank-statements/upload          — upload a PDF (raw body)
//   GET  /api/bank-statements/:id             — statement + lines
//   DELETE /api/bank-statements/:id           — delete a statement + linked ledger rows
//   GET  /api/bank-statements/:id/candidates  — dropdown master-data
//   GET  /api/bank-statements/candidates      — same master-data, global
//   POST /api/bank-statements/:id/reconcile   — save (draft / finalize)
//   POST /api/bank-statements/cash            — record a manual cash payment
//   PUT  /api/bank-statements/cash/:txnId     — edit a cash entry
//   DELETE /api/bank-statements/cash/:txnId   — remove a cash entry
// ---------------------------------------------------------------------------

interface Envelope<T> {
  success: true;
  data: T;
}

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export type ReconStatus = 'UNMATCHED' | 'AUTO_MATCHED' | 'MANUAL_MATCHED' | 'IGNORED';
export type ReconConfidence = 'HIGH' | 'MEDIUM' | 'LOW';
// Strict transaction direction (CLE-164): credit -> INCOME, debit -> OUTCOME.
// Ignore behaviour lives in ReconStatus ('IGNORED'), not here.
export type ReconType = 'INCOME' | 'OUTCOME';
// Settlement channel: auto-detected at import (BANK_TRANSFER/CARD), CASH for
// manual cash-register entries; editable per line in the reconcile UI.
export type PaymentMethod = 'BANK_TRANSFER' | 'CARD' | 'CASH';

export interface StatementSummary {
  id: string;
  // Firm the statement belongs to (multi-company); null = default firm.
  companyId: string | null;
  company: { id: string; name: string } | null;
  // Bank account this statement was linked to (by IBAN match at upload).
  bankAccountId: string | null;
  bankAccount: { id: string; iban: string; label: string | null; bankName: string } | null;
  bankName: string;
  bankCode: string;
  iban: string | null;
  accountHolder: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  openingBalance: number | null;
  closingBalance: number | null;
  // Statement-level credit/debit totals: PDF header values when parsed, else
  // (list only) the sum of the statement's non-duplicate lines as fallback.
  totalCredits: number | null;
  totalDebits: number | null;
  status: 'DRAFT' | 'PARTIALLY_RECONCILED' | 'RECONCILED';
  originalFilename: string | null;
  transactionCount: number;
  parsedAt: string | null;
  reconciledAt: string | null;
  createdAt: string;
}

export interface StatementTransaction {
  id: string;
  date: string;
  processingDate: string | null;
  counterparty: string | null;
  counterpartyIban: string | null;
  counterpartyBank: string | null;
  // CUI/CIF parsed from the line description (CLE-165); resolved to the
  // counterparty name via ANAF on import and shown in the raw-data Info popover.
  counterpartyCui: string | null;
  // Clean, human-readable title (CLE-156): the cleaned supplier/counterparty
  // name or a short summary — shown as the line title instead of the raw
  // `description`, which is kept for the collapsible reference section.
  title: string | null;
  description: string | null;
  type: 'credit' | 'debit';
  amount: number;
  currency: string;
  originalAmount: number | null;
  originalCurrency: string | null;
  exchangeRate: number | null;
  bankReference: string | null;
  balance: number | null;
  invoiceReference: string | null;
  paymentMethod: PaymentMethod;
  // De-duplication (CLE-152). isDuplicate = auto-skipped duplicate; verdict is
  // 'duplicate' | 'possible' | null; originalTransactionId points at the row
  // this one duplicates.
  isDuplicate: boolean;
  duplicateVerdict: 'duplicate' | 'possible' | null;
  originalTransactionId: string | null;
  // Internal-transfer detection (IBAN-based or regex fallback).
  isInternalTransfer: boolean;
  internalTransferAccountId: string | null;
  reconciliationStatus: ReconStatus;
  reconciliationConfidence: ReconConfidence | null;
  reconciliationType: ReconType | null;
  // Provenance (spec pas 4): who decided the line last, and the import batch
  // that wrote it (kept after a person edits it).
  reconciliationSource: ReconSource | null;
  importBatchId: string | null;
  matchedClientId: string | null;
  matchedSupplierId: string | null;
  matchedPersonId: string | null;
  matchedProjectId: string | null;
  matchedInvoiceId: string | null;
  // The invoices this line pays and how much goes to each (several invoices
  // per payment, several payments per invoice).
  allocations: InvoiceAllocation[];
  // Subscription match: POS/CARD purchases matched by merchant pattern.
  matchedSubscriptionId: string | null;
  // Services nomenclature: optional manual tag (loose ref, no FK).
  matchedServiceId: string | null;
  expenseCategoryId: string | null;
  matchReason: string | null;
  // Historical suggestion from past statements for this counterparty (CLE-153),
  // or null when it has never been categorised before. `via` is how we matched:
  // 'iban' (exact) | 'name' (exact) | 'fuzzy'.
  suggestion: CounterpartySuggestion | null;
  // Administrator flag (CLE-ADMIN): set on lines paid to (DEBIT) or received
  // from (CREDIT) a company administrator during their mandate. The matcher
  // also pre-selects the loan categories; the UI shows a badge plus
  // direction-aware quick-pick buttons.
  administratorMatch: AdministratorMatch | null;
}

/** Payment to / loan from a company administrator during their mandate (CLE-ADMIN). */
export interface AdministratorMatch {
  personId: string;
  fullName: string;
  role: 'ADMINISTRATOR' | 'ASOCIAT_UNIC';
  via: 'iban' | 'name';
}

/** Pre-fill hint from a previous statement's categorisation of this counterparty. */
export interface CounterpartySuggestion {
  expenseCategoryId: string | null;
  expenseCategoryCode: string | null;
  matchedSupplierId: string | null;
  matchedClientId: string | null;
  matchedPersonId: string | null;
  matchedProjectId: string | null;
  matchedSubscriptionId: string | null;
  matchedServiceId: string | null;
  reconciliationType: ReconType | null;
  occurrenceCount: number;
  via: 'iban' | 'name' | 'fuzzy';
  alsoSeenCategories: string[];
}

export interface StatementDetail extends StatementSummary {
  transactions: StatementTransaction[];
  // Signed proxy URL to the original PDF in Drive; null when storage is off
  // or the statement has no stored PDF (e.g. the cash register). Expires ~1h.
  pdfUrl: string | null;
}

export interface ListResult {
  rows: StatementSummary[];
  // Credit/debit sums over the whole filtered set (all pages), from
  // non-duplicate lines.
  totals: { credits: number; debits: number };
  meta: { page: number; limit: number; total: number; totalPages: number };
}

/** One skipped/flagged duplicate line surfaced after an upload (CLE-152). */
export interface DuplicateInfo {
  date: string;
  amount: number;
  type: 'credit' | 'debit';
  counterparty: string | null;
  description: string | null;
  verdict: 'duplicate' | 'possible';
  originalImportedAt: string | null;
}

export interface UploadResult {
  statementId: string;
  bankCode: string;
  // Firm assignment (multi-company): 'explicit' = picked at upload, 'cui' =
  // auto-matched by the CUI parsed from the PDF, 'bank_account' = inherited
  // from matched bank account, 'none' = unassigned (default firm).
  companyId: string | null;
  companyMatch: 'explicit' | 'cui' | 'bank_account' | 'none';
  // Bank account resolution: 'existing' = matched by IBAN, 'new_suggested' =
  // IBAN not known yet, 'none' = no IBAN in statement.
  bankAccountId: string | null;
  bankAccountMatch: 'existing' | 'new_suggested' | 'none';
  transactionCount: number;
  importedCount: number;
  duplicatesSkipped: number;
  possibleDuplicates: number;
  autoMatched: number;
  previousUpload: { uploadedAt: string; fileName: string | null } | null;
  duplicates: DuplicateInfo[];
  warnings: string[];
}

export interface ReconCandidatesDto {
  clients: Array<{ id: string; name: string }>;
  suppliers: Array<{
    id: string;
    name: string;
    isRecurring: boolean;
    expenseCategoryId: string | null;
  }>;
  people: Array<{ id: string; name: string }>;
  invoices: Array<{
    id: string;
    number: string;
    partnerName: string | null;
    /** Still to be paid. */
    total: number;
    invoiceTotal: number;
    currency: string;
    issueDate: string | null;
    direction: 'credit' | 'debit';
  }>;
  categories: Array<{ id: string; code: string; name: string }>;
  projects: Array<{ id: string; name: string; code: string | null }>;
  subscriptions: Array<{
    id: string;
    name: string;
    category: string;
    supplierId: string | null;
    expenseCategoryId: string | null;
  }>;
}

export interface InvoiceAllocation {
  invoiceId: string;
  invoiceNumber?: string;
  invoiceCurrency?: string;
  invoiceTotal?: number;
  /** In the line's currency. */
  amount: number;
  /** Same money in the invoice's currency, when it differs. */
  invoiceAmount?: number | null;
}

export interface ReconcileLine {
  id: string;
  reconciliationStatus: ReconStatus;
  reconciliationType?: ReconType | null;
  matchedClientId?: string | null;
  matchedSupplierId?: string | null;
  matchedPersonId?: string | null;
  matchedProjectId?: string | null;
  matchedInvoiceId?: string | null;
  matchedSubscriptionId?: string | null;
  matchedServiceId?: string | null;
  expenseCategoryId?: string | null;
  // Settlement channel override; null/undefined keeps the stored value.
  paymentMethod?: PaymentMethod | null;
  // The invoices the line pays; sent as-is (omitted = matchedInvoiceId alone).
  allocations?: Pick<InvoiceAllocation, 'invoiceId' | 'amount' | 'invoiceAmount'>[];
}

/** A stored line as the reconcile endpoint takes it back (unchanged decisions). */
export function toReconcileLine(t: StatementTransaction): ReconcileLine {
  return {
    id: t.id,
    reconciliationStatus: t.reconciliationStatus,
    reconciliationType: t.reconciliationType,
    matchedClientId: t.matchedClientId,
    matchedSupplierId: t.matchedSupplierId,
    matchedPersonId: t.matchedPersonId,
    matchedProjectId: t.matchedProjectId,
    matchedInvoiceId: t.matchedInvoiceId,
    // A line matched before allocations existed pays its invoice in full.
    allocations:
      t.allocations.length > 0
        ? t.allocations
        : t.matchedInvoiceId
          ? [{ invoiceId: t.matchedInvoiceId, amount: t.amount }]
          : [],
    matchedSubscriptionId: t.matchedSubscriptionId,
    matchedServiceId: t.matchedServiceId,
    expenseCategoryId: t.expenseCategoryId,
    paymentMethod: t.paymentMethod,
  };
}

export interface ReconcileResult {
  statementId: string;
  status: string;
  finalized: boolean;
  recorded: number;
}

export interface RefreshMatchingResult {
  statementId: string;
  newMatches: number;
  transactions: StatementTransaction[];
}

// Columns the list can be ordered by; "-field" = descending (mirrors backend).
export type StatementSortField =
  | 'bankName'
  | 'iban'
  | 'periodStart'
  | 'transactionCount'
  | 'totalCredits'
  | 'totalDebits'
  | 'status'
  | 'createdAt';
export type StatementSortToken = StatementSortField | `-${StatementSortField}`;

export interface StatementListOptions {
  sort?: StatementSortToken;
  /** Inclusive `YYYY-MM-DD` bounds on the statement period. */
  from?: string;
  to?: string;
  /** Only statements with lines decided this way („din import”). */
  source?: ReconSource;
  /** Only statements with lines from this import batch. */
  importBatchId?: string;
}

/** Who decided a line last: the matcher, a person, or the reconciliation import. */
export type ReconSource = 'auto' | 'manual' | 'import';

export async function listStatements(
  page = 1,
  limit = 20,
  companyId?: string,
  options: StatementListOptions = {},
): Promise<ListResult> {
  const qs = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (companyId) qs.set('companyId', companyId);
  if (options.sort) qs.set('sort', options.sort);
  if (options.from) qs.set('from', options.from);
  if (options.to) qs.set('to', options.to);
  if (options.source) qs.set('source', options.source);
  if (options.importBatchId) qs.set('importBatchId', options.importBatchId);
  const res = await apiFetch<Envelope<ListResult>>(`/api/bank-statements?${qs.toString()}`, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function getStatement(id: string): Promise<StatementDetail> {
  const res = await apiFetch<Envelope<StatementDetail>>(`/api/bank-statements/${id}`, {
    headers: authHeaders(),
  });
  return res.data;
}

export interface DeleteStatementResult {
  deletedTransactions: number;
  deletedFinancials: number;
  revertedInvoices: number;
}

/** Delete a statement together with its [bank:…]-tagged Financial rows; invoices
 * settled by its CREDIT lines are reverted to SENT. */
export async function deleteStatement(id: string): Promise<DeleteStatementResult> {
  const res = await apiFetch<Envelope<DeleteStatementResult>>(`/api/bank-statements/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  return res.data;
}

export async function getCandidates(id: string): Promise<ReconCandidatesDto> {
  const res = await apiFetch<Envelope<ReconCandidatesDto>>(
    `/api/bank-statements/${id}/candidates`,
    { headers: authHeaders() },
  );
  return res.data;
}

/** Statement-agnostic master-data, used by the cash payment modal. */
export async function getGlobalCandidates(): Promise<ReconCandidatesDto> {
  const res = await apiFetch<Envelope<ReconCandidatesDto>>(`/api/bank-statements/candidates`, {
    headers: authHeaders(),
  });
  return res.data;
}

// --- Cash payments (cash register) ------------------------------------------

export interface CashPaymentInput {
  /** ISO yyyy-mm-dd. */
  date: string;
  direction: ReconType;
  amount: number;
  currency?: string;
  counterpartyName?: string | null;
  matchedPersonId?: string | null;
  matchedSupplierId?: string | null;
  matchedClientId?: string | null;
  matchedProjectId?: string | null;
  expenseCategoryId?: string | null;
  description?: string | null;
}

export interface CashPaymentResult {
  statementId: string;
  transactionId: string;
}

export async function createCashPayment(input: CashPaymentInput): Promise<CashPaymentResult> {
  const res = await apiFetch<Envelope<CashPaymentResult>>(`/api/bank-statements/cash`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

export async function updateCashPayment(
  txnId: string,
  input: CashPaymentInput,
): Promise<CashPaymentResult> {
  const res = await apiFetch<Envelope<CashPaymentResult>>(`/api/bank-statements/cash/${txnId}`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

export async function deleteCashPayment(txnId: string): Promise<{ statementId: string }> {
  const res = await apiFetch<Envelope<{ statementId: string }>>(
    `/api/bank-statements/cash/${txnId}`,
    { method: 'DELETE', headers: authHeaders() },
  );
  return res.data;
}

export async function reconcileStatement(
  id: string,
  lines: ReconcileLine[],
  finalize: boolean,
): Promise<ReconcileResult> {
  const res = await apiFetch<Envelope<ReconcileResult>>(`/api/bank-statements/${id}/reconcile`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({
      finalize,
      // Rows still being filled in (no invoice / no amount) are not sent.
      lines: lines.map((l) =>
        l.allocations
          ? {
              ...l,
              allocations: l.allocations
                .filter((a) => a.invoiceId && a.amount > 0)
                .map((a) => ({
                  invoiceId: a.invoiceId,
                  amount: a.amount,
                  invoiceAmount: a.invoiceAmount ?? null,
                })),
            }
          : l,
      ),
    }),
  });
  return res.data;
}

/** Reassign a statement to another firm (null = default). Rejected with 409
 * STATEMENT_FINALIZED once the statement is RECONCILED. */
export async function reassignStatementCompany(
  id: string,
  companyId: string | null,
): Promise<{ statementId: string; companyId: string | null }> {
  const res = await apiFetch<Envelope<{ statementId: string; companyId: string | null }>>(
    `/api/bank-statements/${id}/company`,
    { method: 'PATCH', headers: authHeaders(), body: JSON.stringify({ companyId }) },
  );
  return res.data;
}

/** Re-run auto-match for still-unmatched lines against current clients + suppliers (CLE-151). */
export async function refreshMatching(id: string): Promise<RefreshMatchingResult> {
  const res = await apiFetch<Envelope<RefreshMatchingResult>>(
    `/api/bank-statements/${id}/refresh-matching`,
    { method: 'POST', headers: authHeaders() },
  );
  return res.data;
}

/** Upload a PDF as the raw request body (backend uses express.raw).
 * `companyId` pins the statement to a firm; omitted = auto-detect by CUI. */
export async function uploadStatement(
  file: File,
  companyId?: string,
  onProgress?: UploadProgressHandler,
): Promise<UploadResult> {
  const companyQs = companyId ? `&companyId=${encodeURIComponent(companyId)}` : '';
  const path = `/api/bank-statements/upload?fileName=${encodeURIComponent(file.name)}${companyQs}`;
  // The server parses the PDF before it answers, so the tail of this call is
  // usually longer than the transfer. `onProgress` reports the transfer, then
  // flips to the indeterminate "processing" phase for that tail.
  const envelope = await postFileWithProgress<Envelope<UploadResult>>(`${apiBase()}${path}`, file, {
    headers: { ...authHeaders(), 'Content-Type': file.type || 'application/pdf' },
    onProgress,
    fallbackError: (status) => `Upload failed (${status})`,
  });
  return envelope.data;
}
